import { IMenuItem } from '@dfl/mui-react-common';
import {
  AssessmentOutlined,
  GppGoodOutlined,
  MonetizationOnOutlined,
  SettingsOutlined,
  TravelExploreOutlined,
} from '@mui/icons-material';
import { WAREHOUSE_PERMISSIONS } from 'modules/inventory/warehouse/constants';
import { ROOT_MENU_ENUM } from './menus.enum';
import { ClientIcon } from 'modules/crm/common/components/icons';
import ProductIcon from 'modules/inventory/common/components/Icons/ProductIcon';
import { HomeRootIcon } from 'modules/common/components/icons';
import { ORDER_PERMISSIONS, SUBORDER_PERMISSIONS } from 'modules/sales/common/constants/order-permissions';
import { DISPATCH_PERMISSIONS } from 'modules/sales/dispatch/constants';
import { INCIDENCE_PERMISSIONS } from 'modules/sales/incidence/constants';
import { INCIDENCE_RESOLUTION_PERMISSIONS } from 'modules/sales/incidence-resolution/constants';
import { OFFER_PERMISSIONS } from 'modules/sales-offer/offer/constants';
import { UnPaidOrderPermissions } from 'modules/sales/pre-order/constants/pre-orders.permissions';
import { ORDER_STATUS_PERMISSIONS } from 'modules/sales/settings/order-status/constants';
import { RESOLUTION_STATUS_PERMISSIONS } from 'modules/sales/settings/resolution-status/constants/resolution-status.permissions';
import { CAUSES_INCIDENCE_PERMISSIONS } from 'modules/sales/settings/causes-incidence/constants';
import { PRODUCT_PERMISSIONS } from 'modules/inventory/product/constants';
import { DISTRIBUTION_CENTER_PERMISSIONS } from 'modules/inventory/distribution-centers/constants';
import { SUPPLIER_PERMISSIONS } from 'modules/inventory/provider/supplier/constants';
import { LOGISTICS_PERMISSIONS } from 'modules/inventory/provider/logistics/constants';
import { CARRIER_PERMISSIONS } from 'modules/inventory/provider/carriers/constants';
import { CATEGORY_PERMISSIONS } from 'modules/inventory/settings/category/constants';
import { WAREHOUSE_AREA_PERMISSIONS } from 'modules/inventory/settings/warehouse-area/constants';
import { TAGS_PERMISSIONS } from 'modules/inventory/settings/tags/constants';
import { STOCK_REDUCTION_CAUSE_PERMISSIONS } from 'modules/inventory/settings/stock-reduction-cause/constants';
import { COLLECTIONS_PERMISSIONS } from 'modules/cms/collections/constants';
import { PAGE_PERMISSIONS } from 'modules/cms/page/constants';
import { TESTIMONY_PERMISSIONS } from 'modules/cms/testimony/constants';
import { KEYWORD_PERMISSIONS } from 'modules/cms/seo/keyword/constants';
import { MEDIA_PERMISSIONS } from 'modules/cms/medias/constants/medias.permissions';
import { CLIENTS_PERMISSIONS } from 'modules/crm/clients/constants';
import { ADDRESS_BOOK_PERMISSIONS } from 'modules/crm/address-book/constants';
import { REPORT_CAUSE_PERMISSIONS } from 'modules/crm/settings/report-cause/constants';
import { DISALLOWED_WORD_PERMISSIONS } from 'modules/crm/settings/disallowed-word/constants';
import { ACCOUNT_DELETION_REASON_PERMISSIONS } from 'modules/crm/settings/account-deletion-reason/constants';
import { CONCILIATION_PERMISSIONS } from 'modules/conciliation/main/constants';
import { RECONCILIATION_ADJUSTMENT_PERMISSIONS } from 'modules/conciliation/reconciliation-adjustment/constants/reconciliation-adjustment.permissions';
import { PAYMENT_AGREEMENT_PERMISSIONS } from 'modules/conciliation/payment-agreement/constants';
import { CONCILIATION_ADJUSTMENT_CAUSES_PERMISSIONS } from 'modules/conciliation/conciliation-adjustment-causes/constants';
import { AUTO_ADJUSTMENT_RULE_PERMISSIONS } from 'modules/conciliation/auto-adjustment-rules/constants';
import { PAYMENT_METHOD_PERMISSIONS } from 'modules/sales/settings/payment-settings/constants';
import { BANK_PERMISSIONS } from 'modules/sales/settings/bank/constants';
import { SHIPPING_PERMISSIONS } from 'modules/sales/settings/home-delivery/constants';
import { AI_ASSISTANT_PERMISSIONS } from 'modules/cms/seo/ai-assistant/constants';
import { USER_PERMISSIONS } from 'modules/security/users/constants/user.permissions';
import { ROLE_PERMISSIONS } from 'modules/security/roles/interfaces/permissions';
import { REPORTS_PERMISSIONS } from 'modules/dashboard/constant/reports.permissions';
import { REVIEWS_PERMISSIONS } from 'modules/crm/reviews/constants';
import { FRAUD_PERMISSIONS } from 'modules/cms/fraud-integration/constants/fraud.permissions';
import { TRACKER_PERMISSIONS } from 'modules/cms/tracker-integration/constants/tracker-assistant.permissions';
import { SEO_PERMISSIONS } from 'modules/cms/seo/common/constants/seo.permissions';
import { disableConciliation } from 'utils/flags-utils';
import { CUSTOMER_CARE_ACTION_PERMISSIONS } from 'modules/reports/constants/customer-care.permissions';

export type MenuProps = IMenuItem & {
  menuType: ROOT_MENU_ENUM;
  stepTour: string;
};

export const ROOT_MENU: MenuProps[] = [
  {
    title: 'main_menu.admin.section.general.home',
    path: '/?createdAt=LAST-SEVEN-DAYS',
    icon: <HomeRootIcon fontSize='small' />,
    menuType: ROOT_MENU_ENUM.HOME,
    stepTour: '1',
  },
  {
    title: 'main_menu.admin.section.sales.title',
    path: '/sales/orders',
    partialMatch: true,
    icon: <MonetizationOnOutlined fontSize='small' />,
    menuType: ROOT_MENU_ENUM.SALES,
    stepTour: '3',
    permissions: [
      ORDER_PERMISSIONS.ORDER_VIEW,
      SUBORDER_PERMISSIONS.SUBORDER_VIEW,
      DISPATCH_PERMISSIONS.DISPATCH_VIEW,
      INCIDENCE_PERMISSIONS.INCIDENCE_VIEW,
      INCIDENCE_RESOLUTION_PERMISSIONS.INCIDENCE_RESOLUTION_VIEW,
      OFFER_PERMISSIONS.OFFER_VIEW,
      UnPaidOrderPermissions.UNPAID_ORDER_VIEW,
      ORDER_STATUS_PERMISSIONS.ORDER_STATUS_VIEW,
      RESOLUTION_STATUS_PERMISSIONS.RESOLUTION_STATUS_VIEW,
      CAUSES_INCIDENCE_PERMISSIONS.CAUSES_INCIDENCE_VIEW,
      CUSTOMER_CARE_ACTION_PERMISSIONS.VIEW,
    ],
    atLessOne: true,
  },
  {
    title: 'main_menu.admin.section.inventory.title',
    path: '/inventory/products',
    partialMatch: true,
    icon: <ProductIcon fontSize='small' />,
    menuType: ROOT_MENU_ENUM.INVENTORY,
    stepTour: '2',
    permissions: [
      PRODUCT_PERMISSIONS.PRODUCT_VIEW,
      WAREHOUSE_PERMISSIONS.WAREHOUSE_VIEW,
      DISTRIBUTION_CENTER_PERMISSIONS.DISTRIBUTION_CENTER_VIEW,
      SUPPLIER_PERMISSIONS.SUPPLIER_VIEW,
      LOGISTICS_PERMISSIONS.LOGISTICS_VIEW,
      CARRIER_PERMISSIONS.CARRIER_VIEW,
      CATEGORY_PERMISSIONS.CATEGORY_VIEW,
      WAREHOUSE_AREA_PERMISSIONS.WAREHOUSE_AREA_VIEW,
      TAGS_PERMISSIONS.TAGS_VIEW,
      STOCK_REDUCTION_CAUSE_PERMISSIONS.STOCK_REDUCTION_CAUSE_VIEW,
    ],
    atLessOne: true,
  },
  {
    title: 'main_menu.admin.section.clients.clients',
    path: '/crm/clients',
    partialMatch: true,
    icon: <ClientIcon fontSize='small' />,
    menuType: ROOT_MENU_ENUM.CRM,
    stepTour: '4',
    permissions: [
      CLIENTS_PERMISSIONS.CLIENTS_VIEW,
      REVIEWS_PERMISSIONS.REVIEWS_VIEW,
      ADDRESS_BOOK_PERMISSIONS.ADDRESS_BOOK_VIEW,
      REPORT_CAUSE_PERMISSIONS.REPORT_CAUSE_VIEW,
      DISALLOWED_WORD_PERMISSIONS.DISALLOWED_WORD_VIEW,
      ACCOUNT_DELETION_REASON_PERMISSIONS.ACCOUNT_DELETION_REASON_VIEW,
    ],
    atLessOne: true,
  },
  {
    title: 'main_menu.admin.section.cms.title',
    path: '/cms/collections/banners',
    partialMatch: true,
    icon: <TravelExploreOutlined fontSize='small' />,
    menuType: ROOT_MENU_ENUM.CMS,
    stepTour: '5',
    permissions: [
      COLLECTIONS_PERMISSIONS.COLLECTIONS_VIEW,
      TESTIMONY_PERMISSIONS.TESTIMONY_VIEW,
      PAGE_PERMISSIONS.PAGE_VIEW,
      MEDIA_PERMISSIONS.MEDIA_VIEW,
      KEYWORD_PERMISSIONS.KEYWORD_VIEW,
      SEO_PERMISSIONS.SEO_VIEW,
    ],
    atLessOne: true,
  },
  ...(!disableConciliation ? [{
    title: 'main_menu.admin.section.sales.conciliations',
    path: '/conciliations/main/general',
    partialMatch: true,
    icon: <MonetizationOnOutlined fontSize='small' />,
    menuType: ROOT_MENU_ENUM.CONCILIATION,
    stepTour: '6',
    permissions: [
      CONCILIATION_PERMISSIONS.CONCILIATION_VIEW,
      RECONCILIATION_ADJUSTMENT_PERMISSIONS.RECONCILIATION_ADJUSTMENT_VIEW,
      PAYMENT_AGREEMENT_PERMISSIONS.PAYMENT_AGREEMENT_VIEW,
      CONCILIATION_ADJUSTMENT_CAUSES_PERMISSIONS.CONCILIATION_ADJUSTMENT_CAUSES_VIEW,
      AUTO_ADJUSTMENT_RULE_PERMISSIONS.AUTO_ADJUSTMENT_VIEW,
    ],
    atLessOne: true,
  }] : []),
  {
    title: 'main_menu.admin.section.reports.title',
    path: '/reports/sales/orders',
    atLessOne: true,
    icon: <AssessmentOutlined fontSize='small' />,
    menuType: ROOT_MENU_ENUM.REPORTS,
    stepTour: '7',
    permissions: [
      REPORTS_PERMISSIONS.REPORT_ORDERS_PAID,
      REPORTS_PERMISSIONS.REPORT_OFFERS,
      REPORTS_PERMISSIONS.REPORT_INCIDENCES,
      REPORTS_PERMISSIONS.REPORT_PRODUCT,
      REPORTS_PERMISSIONS.REPORT_PRODUCT_ROTATION,
      REPORTS_PERMISSIONS.REPORT_LOGISTIC_LOCATION,
      REPORTS_PERMISSIONS.REPORT_CLIENTS,
      REPORTS_PERMISSIONS.REPORT_SEO_AUDIT,
      REPORTS_PERMISSIONS.REPORT_CANCELLED_ORDERS_READ,
    ],
  },
  {
    title: 'main_menu.admin.section.settings.title',
    path: '/settings/payment-settings',
    atLessOne: true,
    icon: <SettingsOutlined fontSize='small' />,
    menuType: ROOT_MENU_ENUM.SETTINGS,
    stepTour: '8',
    permissions: [
      PAYMENT_METHOD_PERMISSIONS.PAYMENT_METHOD_VIEW,
      PAYMENT_METHOD_PERMISSIONS.PAYMENT_FACTURATION_CONFIG,
      BANK_PERMISSIONS.BANK_VIEW,
      SHIPPING_PERMISSIONS.SHIPPING_VIEW,
      AI_ASSISTANT_PERMISSIONS.AI_ASSISTANT_CONFIG,
      FRAUD_PERMISSIONS.INTEGRATION_DAILYFRAUD,
      TRACKER_PERMISSIONS.TRACKER_INTEGRATION_CONFIG,
    ],
  },
  {
    title: 'main_menu.admin.section.security.title',
    path: '/security/system-users/all',
    icon: <GppGoodOutlined fontSize='small' />,
    menuType: ROOT_MENU_ENUM.SECURITY,
    stepTour: '9',
    permissions: [USER_PERMISSIONS.ADMIN, USER_PERMISSIONS.PROVIDER_USER_VIEW, ROLE_PERMISSIONS.ROLE_VIEW],
    atLessOne: true,
  },
];
